import { PDFDocument, StandardFonts } from "pdf-lib"

import { comoFecha, comoFechaHora } from "@/lib/fechas"

import {
  ALTO,
  ANCHO,
  escribible,
  GRIS,
  LINEA,
  type Lienzo,
  lineas,
  MARGEN,
  membrete,
  nuevaPagina,
  texto,
  UTIL,
} from "./pdfReporte"
import type { Expedientes } from "./tiposExpedientes"

/**
 * El PDF de los expedientes abiertos por estatus, compuesto en el navegador (13/10). El
 * mismo papel, tinta y membrete que el reporte de cuentas por pagar (`pdfReporte.ts`):
 * un bloque por estatus con expediente, cliente y ETA.
 */

const COLUMNAS = [
  { rotulo: "Expediente", ancho: 90 },
  { rotulo: "Cliente", ancho: 0 },
  { rotulo: "ETA", ancho: 80 },
]
const CLIENTE = UTIL - COLUMNAS.reduce((s, c) => s + c.ancho, 0) - 2 * 6
const ANCHOS = COLUMNAS.map((c) => (c.rotulo === "Cliente" ? CLIENTE : c.ancho))

function cabecera(l: Lienzo) {
  let x = MARGEN
  COLUMNAS.forEach((c, i) => {
    texto(l, c.rotulo, x, l.y, { cuerpo: 7.5, fuente: l.negrita, color: GRIS })
    x += (ANCHOS[i] ?? 0) + 6
  })
  l.y -= 5
  l.pagina.drawLine({
    start: { x: MARGEN, y: l.y },
    end: { x: ANCHO - MARGEN, y: l.y },
    thickness: 0.5,
    color: LINEA,
  })
  l.y -= 11
}

export async function pdfDeLosExpedientes(
  e: Expedientes,
  logo: Uint8Array | null,
): Promise<Uint8Array<ArrayBuffer>> {
  const pdf = await PDFDocument.create()
  pdf.setTitle(e.titulo)
  const l: Lienzo = {
    pdf,
    pagina: pdf.addPage([ANCHO, ALTO]),
    y: ALTO - MARGEN,
    normal: await pdf.embedFont(StandardFonts.Helvetica),
    negrita: await pdf.embedFont(StandardFonts.HelveticaBold),
  }
  membrete(l, e, logo ? await pdf.embedPng(logo) : null)
  texto(l, e.titulo, MARGEN, l.y, { cuerpo: 16, fuente: l.negrita })
  l.y -= 15
  texto(
    l,
    `Reporte del ${comoFecha(e.fecha)}, generado el ${comoFechaHora(e.generado_en)}`,
    MARGEN,
    l.y,
    { color: GRIS },
  )
  l.y -= 20
  texto(l, `Expedientes abiertos: ${e.total}`, MARGEN, l.y, { cuerpo: 10, fuente: l.negrita })
  l.y -= 26

  for (const g of e.grupos) {
    if (l.y < MARGEN + 70) nuevaPagina(l)
    const encabezar = (sigue: boolean) => {
      texto(l, `${g.estatus} (${g.expedientes.length})${sigue ? " (continúa)" : ""}`, MARGEN, l.y, {
        cuerpo: 10.5,
        fuente: l.negrita,
      })
      l.y -= 16
      cabecera(l)
    }
    encabezar(false)
    for (const x of g.expedientes) {
      const cliente = lineas(l.normal, 8.5, x.cliente, CLIENTE)
      const alto = cliente.length * 10.5 + 6
      if (l.y - alto < MARGEN + 14) {
        nuevaPagina(l)
        encabezar(true)
      }
      texto(l, x.numero, MARGEN, l.y, { fuente: l.negrita })
      cliente.forEach((ln, k) => {
        texto(l, ln, MARGEN + (ANCHOS[0] ?? 0) + 6, l.y - k * 10.5)
      })
      texto(l, comoFecha(x.eta) || "—", MARGEN + (ANCHOS[0] ?? 0) + CLIENTE + 12, l.y)
      l.y -= alto
      l.pagina.drawLine({
        start: { x: MARGEN, y: l.y + 8 },
        end: { x: ANCHO - MARGEN, y: l.y + 8 },
        thickness: 0.4,
        color: LINEA,
      })
    }
    l.y -= 14
  }

  const paginas = pdf.getPages()
  paginas.forEach((p, i) => {
    const pie = escribible(
      l.normal,
      `${e.empresa_nombre} · ${e.titulo} · página ${i + 1} de ${paginas.length}`,
    )
    p.drawText(pie, {
      x: ANCHO - MARGEN - l.normal.widthOfTextAtSize(pie, 7),
      y: MARGEN / 2,
      size: 7,
      font: l.normal,
      color: GRIS,
    })
  })
  return new Uint8Array(await pdf.save())
}
